import { useEffect, useState } from 'react';
import css from './index.module.scss';
import { Alert, Button, IconNode, Loader, Typography } from '@components/base';
import { LinkButton, Seo, TextField } from '@components/common';
import { CLIENT_ROUTES } from '@router/routes';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ILoginCredential } from '@helpers/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from '@helpers/yup';
import Images from '@assets/images';
import { sendOtp, userLogin } from '@services/login.service';
import notify from '@helpers/toastify-helper';
import SessionService from '@helpers/session-helper';
import { USER_TYPES } from '@helpers/constants';
import { useNavigate } from 'react-router-dom';

interface SignUpState {
  showLoader: boolean;
  showErrorMessage: boolean;
  isOtpSent: boolean;
  timerisActive: boolean;
}

const getErrorMessage = (response: any, fallbackMessage: string) => {
  const messageCandidate = response?.error?.message || response?.data?.error?.message;
  if (typeof messageCandidate === 'string' && messageCandidate.trim()) return messageCandidate;
  return fallbackMessage;
};

const SignUp = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState(0);
  const [signUpState, setSignUpState] = useState<SignUpState>({
    showLoader: false,
    showErrorMessage: false,
    isOtpSent: false,
    timerisActive: false
  });

  const { isOtpSent, timerisActive } = signUpState;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      otp: '',
      loginVia: 'otp_request'
    }
  });

  const sendOtpToUser = async (email: string) => {
    if (!email) return;
    setSignUpState((prevState) => ({ ...prevState, showErrorMessage: false, showLoader: true }));
    try {
      const response = await sendOtp(email);
      if (response?.success) {
        setSignUpState((prevState) => ({ ...prevState, isOtpSent: true }));
        startCountdown();
        notify({
          message: 'OTP sent successfully',
          severity: 'success'
        });
        setValue('loginVia', 'otp_sent');
        return;
      }

      setSignUpState((prevState) => ({ ...prevState, showErrorMessage: true, showLoader: false }));
      notify({
        message: getErrorMessage(response, 'Failed to send OTP'),
        severity: 'error'
      });
    } catch (error: any) {
      setSignUpState((prevState) => ({ ...prevState, showErrorMessage: true }));
      notify({
        message: getErrorMessage(error, 'Failed to send OTP'),
        severity: 'error'
      });
    } finally {
      setSignUpState((prevState) => ({ ...prevState, showLoader: false }));
    }
  };

  const handleFormSubmit: SubmitHandler<ILoginCredential> = async (data) => {
    setSignUpState((prevState) => ({ ...prevState, showErrorMessage: false }));
    if (!isOtpSent) {
      sendOtpToUser(data?.email ?? '');
      return;
    }
    try {
      const body: any = { ...data };
      delete body.password;
      const response = await userLogin(body);
      if (response?.success && response?.data) {
        const {
          access,
          refresh,
          email,
          uidb64,
          token,
          first_time_login,
          user_auth_info,
          role,
          is_finance_team_member
        } = response.data;
        if (first_time_login) {
          navigate(`/${CLIENT_ROUTES.passwordSetup}`, {
            replace: true,
            state: { email, uidb64, token }
          });
          return;
        }
        const user_authorization_data = {
          ...user_auth_info,
          is_finance_team_member: is_finance_team_member,
          user_type: role === USER_TYPES.customer ? USER_TYPES.customer : USER_TYPES.internal
        };
        SessionService.saveSession(access, refresh, user_authorization_data);
      } else {
        setSignUpState((prevState) => ({ ...prevState, showErrorMessage: true }));
        notify({
          message: getErrorMessage(response, 'Failed to verify OTP'),
          severity: 'error'
        });
      }
    } catch (error: any) {
      setSignUpState((prevState) => ({ ...prevState, showErrorMessage: true }));
      notify({
        message: getErrorMessage(error, 'Failed to verify OTP'),
        severity: 'error'
      });
    }
  };

  const startCountdown = () => {
    setTime(30);
    setSignUpState((prevState) => ({ ...prevState, timerisActive: true }));
  };

  const formatTime = (timeValue: number) => {
    const minutes = Math.floor(timeValue / 60);
    const seconds = timeValue % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timerisActive && time > 0) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (time === 0) {
      clearInterval(interval!);
      setSignUpState((prevState) => ({ ...prevState, timerisActive: false }));
      setTime(0);
    }
    return () => clearInterval(interval!);
  }, [timerisActive, time]);

  return (
    <main className={css.signUpSection}>
      <Seo title="Sign Up" />
      <Typography variant="h1">Sign Up</Typography>
      <form className={css.formWrapper} onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              type="email"
              inputMode="email"
              enterKeyHint="next"
              autoComplete="email"
              label="Email"
              placeholder="Enter your email"
              disabled={isOtpSent}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              endIcon={fieldState.invalid && <IconNode src={Images.alertError} alt="Error" />}
            />
          )}
        />
        {isOtpSent && (
          <Controller
            name="otp"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                type="text"
                label="OTP"
                placeholder="Enter OTP sent to your email"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={fieldState.invalid && <IconNode src={Images.alertError} alt="Error" />}
              />
            )}
          />
        )}
        {signUpState.showErrorMessage && (
          <Alert
            severity="error"
            title={isOtpSent ? 'Invalid OTP' : 'Something went wrong!'}
            icon={false}
          >
            {isOtpSent ? `Please re-enter OTP and try again.` : `Please try again later.`}
          </Alert>
        )}
        <div className={css.fieldActionButton}>
          {isOtpSent && (
            <div className={css.otpActionRow}>
              <Button
                variant="text"
                onClick={() => {
                  setSignUpState((prevState) => ({ ...prevState, isOtpSent: false }));
                  setValue('loginVia', 'otp_request');
                }}
              >
                Change Email
              </Button>
              <Button
                variant="text"
                onClick={() => sendOtpToUser(watch('email'))}
                disabled={timerisActive}
              >
                Resend OTP {timerisActive ? `in ${formatTime(time)}` : ``}
              </Button>
            </div>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isOtpSent ? 'Verify OTP' : 'Send OTP'}
          </Button>
          <LinkButton to={`${CLIENT_ROUTES.login}`}>Back to Login</LinkButton>
        </div>
      </form>
      <Loader open={isSubmitting || signUpState.showLoader} />
    </main>
  );
};

export default SignUp;
